import { Fragment, useEffect, useState } from "react";
import { BedDouble, CalendarDays, Save, Sparkles, Users } from "lucide-react";
import { useToast } from "@/shared/providers/ToastProvider";
import { inputClass, selectClass } from "./FormField";
import { PatientCodingSummary } from "./PatientCodingSummary";
import { AffectionPicker } from "./AffectionPicker";
import { PatientSearch } from "./PatientSearch";
import {
  fetchInvoiceLines,
  saveInvoiceAffections,
  saveInvoiceLines,
  suggestInvoiceCodes,
  type CodingWorkflow,
  type InvoiceLine,
  type InvoiceLinesResponse,
  type InvoiceVisit,
  type StayRules,
} from "../api/claimsApi";

type Props = {
  invoiceId: number;
  /** Incrémenté par la page pour recharger après une transmission au Sinistre. */
  refreshKey?: number;
  onWorkflowChange?: (workflow: CodingWorkflow) => void;
};

const STATE_LABEL: Record<CodingWorkflow["state"], string> = {
  coding: "En codification",
  pending: "Chez le Sinistre (lecture seule)",
  controlled: "Contrôlée par le Sinistre (lecture seule)",
  anomaly: "Anomalie : à corriger",
};

function money(value: number): string {
  return value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function day(value: string): string {
  const [y, m, d] = value.split("-");
  return d && m && y ? `${d}/${m}/${y}` : value;
}

function visitLabel(visit: InvoiceVisit): string {
  return visit.kind === "hospitalisation"
    ? `Hospitalisation du ${day(visit.start_date)} au ${day(visit.end_date)}`
    : `Soins du ${day(visit.start_date)}`;
}

/** Règles « par séjour » du package, en clair (« Chambre : 2 max. par séjour, 75 USD par jour »). */
function stayRule(rule: StayRules["rules"][number], currency: string): string {
  const parts: string[] = [];
  if (rule.rate !== null && rule.rate < 100) parts.push(`${rule.rate.toLocaleString("fr-FR")} %`);
  if (rule.unit_ceiling !== null) parts.push(`${money(rule.unit_ceiling)} ${currency} ${rule.unit_basis === "day" ? "par jour" : "par acte"}`);
  if (rule.max_quantity !== null) parts.push(`${rule.max_quantity} max. par séjour`);
  if (rule.period_ceiling !== null) parts.push(`plafond ${money(rule.period_ceiling)} ${currency} par séjour`);
  if (rule.prior_approval !== "none") parts.push("accord préalable");
  return `${rule.label}${parts.length > 0 ? " : " + parts.join(", ") : ""}`;
}

/**
 * Audit, en trois étapes :
 * 1. les fréquentations de chaque patient (une date de soins, ou un séjour d'hospitalisation) et leurs codes
 *    d'affection CIM-10, avec les suggestions de l'IA à accepter et, pour un séjour, les règles du package ;
 * 2. chaque acte : patient, code acte standard (RCS, RB, RPPH…) et réfaction éventuelle ;
 * 3. le regroupement par patient et par code acte. Le rapport assureur reste agrégé, sans nom.
 */
export function InvoiceLinesAssignment({ invoiceId, refreshKey = 0, onWorkflowChange }: Props) {
  const { showToast } = useToast();
  const [data, setData] = useState<InvoiceLinesResponse | null>(null);
  const [lines, setLines] = useState<InvoiceLine[]>([]);
  // Saisie en cours du montant retenu (texte libre), par ligne.
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  // Patients ajoutés par recherche (facture couvrant plusieurs entreprises), pas encore rattachés à un acte.
  const [extraPatients, setExtraPatients] = useState<Array<{ key: string; label: string }>>([]);

  const apply = (response: InvoiceLinesResponse) => {
    setData(response);
    setLines(response.lines);
    setDrafts({});
    onWorkflowChange?.(response.workflow);
  };

  useEffect(() => {
    fetchInvoiceLines(invoiceId)
      .then(apply)
      .catch((err) => setError(err instanceof Error ? err.message : "Chargement impossible."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, refreshKey]);

  if (!data && !error) return null;

  const editable = data?.workflow.editable ?? false;
  const patientOptions = [
    ...(data?.beneficiaries ?? []),
    ...extraPatients.filter((extra) => !(data?.beneficiaries ?? []).some((b) => b.key === extra.key)),
  ];
  const beneficiaryLabel = (key: string) => patientOptions.find((b) => b.key === key)?.label ?? key;

  const setLine = (id: number, patch: Partial<InvoiceLine>) =>
    setLines((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const applyToAll = (beneficiary: string) => setLines((rows) => rows.map((row) => ({ ...row, beneficiary: beneficiary || null })));

  /** Montant retenu : vide ou égal au facturé = pas de réfaction ; 0 = acte entièrement rejeté. */
  const setRetained = (line: InvoiceLine, raw: string) => {
    setDrafts((current) => ({ ...current, [line.id]: raw }));
    const value = Number(raw.replace(",", "."));
    const retained = raw.trim() === "" || Number.isNaN(value) || value >= line.amount ? null : Math.max(0, value);
    setLine(line.id, { retained_amount: retained });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await saveInvoiceLines(
        invoiceId,
        lines.map((line) => ({
          line_id: line.id,
          beneficiary: line.beneficiary,
          standard_act_code: line.standard_act_code,
          visit_id: line.visit_id,
          retained_amount: line.retained_amount,
          refaction_reason: line.refaction_reason,
          refaction_code: line.refaction_code,
          refaction_note: line.refaction_note?.trim() || null,
        })),
      );
      apply(response);
      showToast("Codification enregistrée.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  // Codes d'affection : la liste complète de la facture est réenregistrée à chaque ajout ou retrait.
  const saveAffections = async (next: Array<{ visit_id?: number; beneficiary?: string; icd_code: string }>) => {
    setError(null);
    try {
      const response = await saveInvoiceAffections(invoiceId, next);
      // Le package peut dépendre de l'affection : les prises en charge sont recalculées.
      setData(response);
      setLines((rows) => rows.map((row) => {
        const fresh = response.lines.find((line) => line.id === row.id);
        return fresh ? { ...row, coverage_rate: fresh.coverage_rate, covered_amount: fresh.covered_amount, coverage_note: fresh.coverage_note } : row;
      }));
      onWorkflowChange?.(response.workflow);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement des affections impossible.");
    }
  };
  const currentAffections = () =>
    (data?.affections ?? []).map((a) => (a.visit_id ? { visit_id: a.visit_id, icd_code: a.code } : { beneficiary: a.beneficiary, icd_code: a.code }));
  const addCode = (visitId: number, code: string) => {
    if ((data?.affections ?? []).some((a) => a.visit_id === visitId && a.code === code)) return;
    void saveAffections([...currentAffections(), { visit_id: visitId, icd_code: code }]);
  };
  const removeCode = (visitId: number, code: string) =>
    void saveAffections(currentAffections().filter((a) => !(a.visit_id === visitId && a.icd_code === code)));

  const handleSuggest = async () => {
    setSuggesting(true);
    setError(null);
    try {
      const response = await suggestInvoiceCodes(invoiceId);
      setData(response);
      // Suggestions de code acte ajoutées aux lignes, sans écraser la saisie en cours.
      setLines((rows) => rows.map((row) => ({ ...row, ai_act_code: response.lines.find((line) => line.id === row.id)?.ai_act_code ?? null })));
      showToast(response.message ?? "Suggestions prêtes.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Suggestions impossibles.");
    } finally {
      setSuggesting(false);
    }
  };

  const visits = data?.visits ?? [];
  const visitPatients = Array.from(new Set(visits.map((visit) => visit.beneficiary)));
  const uncoded = lines.filter((line) => !line.beneficiary || !line.standard_act_code).length;
  const missingAffections = visits.filter((visit) => visit.codes.length === 0).length;
  const billedTotal = lines.reduce((sum, line) => sum + line.amount, 0);
  const retainedTotal = lines.reduce((sum, line) => sum + (line.retained_amount ?? line.amount), 0);

  return (
    <section className="theme-card min-w-0 p-5">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <Users size={18} className="text-primary" />
          Codification des actes par patient
        </h2>
        <span className="rounded-full bg-card-muted px-2 py-0.5 text-xs text-muted">
          {data ? STATE_LABEL[data.workflow.state] : ""}
        </span>
      </div>
      {error ? <p className="my-3 text-sm text-red-600">{error}</p> : null}

      {lines.length === 0 ? (
        <p className="mt-3 text-sm text-muted">Aucune ligne d'acte sur cette facture.</p>
      ) : (
        <>
          {/* Étape 1 : fréquentations et codes d'affection */}
          <div className="mb-1 mt-4 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground">1. Fréquentations et codes d'affection (CIM-10)</h3>
            {editable && visits.length > 0 ? (
              <button
                type="button"
                disabled={suggesting}
                onClick={() => void handleSuggest()}
                title="L'IA propose les codes CIM-10 de chaque fréquentation et le code acte des lignes non codées ; rien n'est appliqué sans votre accord."
                className="inline-flex items-center gap-2 rounded-lg border border-primary px-3 py-1.5 text-xs text-primary hover:bg-primary/5 disabled:opacity-50"
              >
                <Sparkles size={14} />
                {suggesting ? "Analyse en cours…" : "Proposer les codes (IA)"}
              </button>
            ) : null}
          </div>
          <p className="mb-2 text-xs text-muted">
            Une fréquentation par passage du patient : une date de soins, ou un séjour d'hospitalisation (nuitées consécutives),
            soumis aux limites « par séjour » de son package. Au moins un code par fréquentation, obligatoire avant la
            transmission au Sinistre ; le premier est le diagnostic principal. Visibles par l'Audit uniquement.
          </p>
          {visits.length === 0 ? (
            <p className="text-xs text-amber-700">Rattachez les actes à un patient (étape 2) : leurs fréquentations apparaîtront ici.</p>
          ) : (
            <div className="space-y-3">
              {visitPatients.map((key) => (
                <div key={key} className="rounded-lg border border-border p-3">
                  <p className="mb-2 text-sm font-semibold text-foreground">{beneficiaryLabel(key)}</p>
                  <div className="grid gap-3 md:grid-cols-2">
                    {visits.filter((visit) => visit.beneficiary === key).map((visit) => (
                      <div key={visit.id} className={`rounded-lg border p-3 ${visit.kind === "hospitalisation" ? "border-primary/40 bg-primary/5" : "border-border"}`}>
                        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                          {visit.kind === "hospitalisation" ? <BedDouble size={15} className="text-primary" /> : <CalendarDays size={15} className="text-muted" />}
                          {visitLabel(visit)}
                          {visit.days !== null ? <span className="rounded-full bg-card px-2 py-0.5 text-[11px] text-muted">{visit.days} jour(s)</span> : null}
                        </p>
                        <p className="mb-2 text-xs text-muted">
                          {visit.lines} acte(s) · facturé {money(visit.billed)} · pris en charge {money(visit.covered)}
                        </p>
                        {visit.diagnosis_on_invoice ? (
                          <p className="mb-2 text-xs text-foreground">Diagnostic écrit sur la facture : <strong>{visit.diagnosis_on_invoice}</strong></p>
                        ) : null}
                        <AffectionPicker
                          patientName={`${beneficiaryLabel(key)} — ${visitLabel(visit)}`}
                          affections={visit.codes.map((c) => ({ ...c, beneficiary: key, visit_id: visit.id }))}
                          editable={editable}
                          onAdd={(code) => addCode(visit.id, code)}
                          onRemove={(code) => removeCode(visit.id, code)}
                        />
                        {editable && visit.suggestions.length > 0 ? (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {visit.suggestions.map((suggestion) => (
                              <button
                                key={suggestion.code}
                                type="button"
                                onClick={() => addCode(visit.id, suggestion.code)}
                                title={suggestion.reason}
                                className="inline-flex items-center gap-1 rounded-full border border-dashed border-primary px-2 py-0.5 text-[11px] text-primary hover:bg-accent/20 hover:border-accent"
                              >
                                <Sparkles size={11} />
                                Accepter {suggestion.code} · {suggestion.label}
                              </button>
                            ))}
                          </div>
                        ) : null}
                        {visit.kind === "hospitalisation" ? (
                          <div className="mt-2 rounded-md bg-card px-2 py-1.5 text-[11px] text-muted">
                            {visit.package ? (
                              <>
                                <span className="font-medium text-foreground">Package {visit.package.name}</span>
                                {visit.package.rules.length > 0 ? (
                                  <ul className="mt-0.5 list-disc pl-4">
                                    {visit.package.rules.map((rule) => (
                                      <li key={`${rule.section}-${rule.label}`}>{stayRule(rule, visit.package?.currency ?? "USD")}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span> : aucune limite propre au séjour.</span>
                                )}
                              </>
                            ) : (
                              <span className="text-amber-700">Agent sans package à la date d'entrée : prise en charge à vérifier.</span>
                            )}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {editable && missingAffections > 0 ? (
            <p className="mt-2 text-xs text-amber-700">{missingAffections} fréquentation(s) sans code d'affection.</p>
          ) : null}
          {editable ? (
            <div className="mt-3">
              <PatientSearch
                invoiceId={invoiceId}
                onPick={(option) => setExtraPatients((current) => (current.some((c) => c.key === option.key) ? current : [...current, option]))}
              />
              {data?.multi_company ? (
                <p className="mt-1 text-xs text-muted">
                  Facture du prestataire couvrant plusieurs entreprises : cherchez ici un patient absent de la liste de l'étape 2.
                </p>
              ) : null}
            </div>
          ) : null}

          {/* Étape 2 : actes */}
          <div className="mb-2 mt-6 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground">2. Actes : patient, code acte et réfaction</h3>
            {editable ? (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <label htmlFor="lines-apply-all" className="text-muted">Patient de toutes les lignes :</label>
                <select id="lines-apply-all" defaultValue="" onChange={(e) => applyToAll(e.target.value)} className={`${selectClass} h-8 w-auto text-xs`}>
                  <option value="">Choisir…</option>
                  {patientOptions.map((option) => (
                    <option key={option.key} value={option.key}>{option.label}</option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
          <p className="mb-2 text-xs text-muted">
            Réfaction : saisissez dans « Retenu » le montant accepté (0 pour rejeter l'acte). La différence avec le
            montant facturé est la réfaction ; indiquez alors son motif et son code COR. « Pris en charge » applique ensuite
            le taux de couverture et les plafonds du package du patient (recalculé à chaque enregistrement).
            {uncoded > 0 ? <span className="text-amber-700"> {uncoded} ligne(s) sans patient ou sans code acte.</span> : null}
          </p>
          <fieldset disabled={!editable} className="min-w-0 overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[56rem] text-sm">
              <thead className="bg-card-muted text-left text-xs uppercase text-muted">
                <tr>
                  <th className="px-2 py-2">Acte</th>
                  <th className="px-2 py-2 text-right">Facturé</th>
                  <th className="px-2 py-2">Patient</th>
                  <th className="px-2 py-2">Fréquentation</th>
                  <th className="px-2 py-2">Code acte</th>
                  <th className="px-2 py-2 text-right">Retenu</th>
                  <th className="px-2 py-2 text-right">Réfacté</th>
                  <th className="px-2 py-2 text-right" title="Taux de couverture et plafonds du package du patient, calculés à l'enregistrement">
                    Pris en charge
                  </th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => {
                  const refracted = line.retained_amount !== null ? line.amount - line.retained_amount : 0;
                  return (
                    <Fragment key={line.id}>
                      <tr className="border-t border-border align-top">
                        <td className="px-2 py-2 text-foreground">
                          {line.label}
                          <span className="block text-xs text-muted">{line.date ?? ""} · qté {line.quantity}</span>
                          {line.diagnosis_label ? (
                            <span className="block text-xs text-foreground" title="Diagnostic écrit sur la facture">Diagnostic : {line.diagnosis_label}</span>
                          ) : null}
                          {line.patient_label || line.patient_ref ? (
                            <span className="block text-xs text-amber-700" title="Patient tel qu'écrit sur la facture">
                              Facturé pour : {[line.patient_label, line.patient_ref].filter(Boolean).join(" · ")}
                            </span>
                          ) : null}
                          {line.patient_check ? (
                            <span
                              className={`mt-1 block max-w-[20rem] rounded px-1.5 py-1 text-[11px] ${
                                line.patient_check.level === "warning" ? "bg-red-50 text-red-700" : "bg-primary/5 text-primary"
                              }`}
                            >
                              {line.patient_check.message}
                              {editable && line.patient_check.suggestion && line.patient_check.suggestion !== line.beneficiary ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const key = line.patient_check?.suggestion ?? null;
                                    if (key && !patientOptions.some((o) => o.key === key)) {
                                      setExtraPatients((current) => [...current, { key, label: line.patient_check?.suggestion_label ?? key }]);
                                    }
                                    setLine(line.id, { beneficiary: key, visit_id: null });
                                  }}
                                  className="ml-1 font-medium underline"
                                >
                                  Rattacher à {line.patient_check.suggestion_label}
                                </button>
                              ) : null}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums">{money(line.amount)}</td>
                        <td className="px-2 py-2">
                          <select
                            aria-label={`Patient de ${line.label}`}
                            value={line.beneficiary ?? ""}
                            onChange={(e) => setLine(line.id, { beneficiary: e.target.value || null, visit_id: null })}
                            className={`${selectClass} h-8 w-full min-w-[11rem] text-xs`}
                          >
                            <option value="">Non rattaché</option>
                            {patientOptions.map((option) => (
                              <option key={option.key} value={option.key}>{option.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <select
                            aria-label={`Fréquentation de ${line.label}`}
                            value={line.visit_id === null ? "" : String(line.visit_id)}
                            disabled={!line.beneficiary}
                            onChange={(e) => setLine(line.id, { visit_id: e.target.value === "" ? null : e.target.value === "new" ? "new" : Number(e.target.value) })}
                            className={`${selectClass} h-8 w-full min-w-[10rem] text-xs`}
                          >
                            <option value="">{line.beneficiary ? "Automatique" : "—"}</option>
                            {visits.filter((visit) => visit.beneficiary === line.beneficiary).map((visit) => (
                              <option key={visit.id} value={visit.id}>{visitLabel(visit)}</option>
                            ))}
                            {line.beneficiary ? <option value="new">Nouvelle fréquentation</option> : null}
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <select
                            aria-label={`Code acte de ${line.label}`}
                            value={line.standard_act_code ?? ""}
                            onChange={(e) => setLine(line.id, { standard_act_code: e.target.value || null })}
                            className={`${selectClass} h-8 w-full min-w-[11rem] text-xs`}
                          >
                            <option value="">Choisir un code…</option>
                            {data?.standard_acts.map((act) => (
                              <option key={act.code} value={act.code}>{act.code} · {act.name.toLowerCase()}</option>
                            ))}
                          </select>
                          {editable && !line.standard_act_code && line.ai_act_code ? (
                            <button
                              type="button"
                              onClick={() => setLine(line.id, { standard_act_code: line.ai_act_code ?? null })}
                              className="mt-1 inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                            >
                              <Sparkles size={11} />
                              Suggestion IA : {line.ai_act_code} (appliquer)
                            </button>
                          ) : null}
                        </td>
                        <td className="px-2 py-2 text-right">
                          <input
                            aria-label={`Montant retenu pour ${line.label}`}
                            inputMode="decimal"
                            value={drafts[line.id] ?? money(line.retained_amount ?? line.amount).replace(/\s/g, "")}
                            onChange={(e) => setRetained(line, e.target.value)}
                            onBlur={() => setDrafts(({ [line.id]: _omit, ...rest }) => rest)}
                            className={`${inputClass} h-8 w-24 text-right tabular-nums`}
                          />
                        </td>
                        <td className={`px-2 py-2 text-right tabular-nums ${refracted > 0 ? "font-semibold text-red-600" : "text-muted"}`}>
                          {refracted > 0 ? `−${money(refracted)}` : "—"}
                        </td>
                        <td className="px-2 py-2 text-right" title={line.coverage_note ?? undefined}>
                          {line.covered_amount !== null ? (
                            <>
                              <span className={`tabular-nums ${line.coverage_note ? "font-semibold text-amber-700" : "text-foreground"}`}>
                                {money(line.covered_amount)}
                              </span>
                              {line.coverage_rate !== null ? (
                                <span className="block text-[11px] text-muted">{line.coverage_rate.toLocaleString("fr-FR")} %</span>
                              ) : null}
                              {line.coverage_note ? <span className="block max-w-[12rem] text-[11px] text-amber-700">{line.coverage_note}</span> : null}
                            </>
                          ) : (
                            <span className="text-[11px] text-muted">{line.coverage_note ?? "—"}</span>
                          )}
                        </td>
                      </tr>
                      {refracted > 0 ? (
                        <tr className="bg-red-50/60">
                          <td colSpan={8} className="px-2 pb-2">
                            <div className="flex flex-wrap items-center gap-2 text-xs">
                              <span className="font-medium text-red-700">Réfaction de {money(refracted)} :</span>
                              <select
                                aria-label={`Motif de réfaction de ${line.label}`}
                                value={line.refaction_reason ?? ""}
                                onChange={(e) => setLine(line.id, { refaction_reason: e.target.value || null })}
                                className={`${selectClass} h-8 w-auto text-xs`}
                              >
                                <option value="">Motif (obligatoire)…</option>
                                {Object.entries(data?.refaction_reasons ?? {}).map(([value, label]) => (
                                  <option key={value} value={value}>{label}</option>
                                ))}
                              </select>
                              <select
                                aria-label={`Code de réfaction de ${line.label}`}
                                value={line.refaction_code ?? ""}
                                onChange={(e) => setLine(line.id, { refaction_code: e.target.value || null })}
                                className={`${selectClass} h-8 w-auto text-xs`}
                              >
                                <option value="">Code COR…</option>
                                {data?.refaction_codes.map((code) => (
                                  <option key={code.code} value={code.code}>{code.code} · {(code.category ?? code.name).toLowerCase()}</option>
                                ))}
                              </select>
                              <input
                                aria-label={`Précision sur la réfaction de ${line.label}`}
                                value={line.refaction_note ?? ""}
                                onChange={(e) => setLine(line.id, { refaction_note: e.target.value })}
                                placeholder="Précision (facultatif)"
                                className={`${inputClass} h-8 min-w-[12rem] flex-1 text-xs`}
                              />
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border text-sm font-semibold">
                  <td className="px-2 py-2">Total</td>
                  <td className="px-2 py-2 text-right tabular-nums">{money(billedTotal)}</td>
                  <td colSpan={3} />
                  <td className="px-2 py-2 text-right tabular-nums">{money(retainedTotal)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-red-600">
                    {billedTotal - retainedTotal > 0.004 ? `−${money(billedTotal - retainedTotal)}` : "—"}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-primary">
                    {money(lines.reduce((sum, line) => sum + (line.covered_amount ?? line.retained_amount ?? line.amount), 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </fieldset>
          {editable ? (
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleSave()}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
              >
                <Save size={15} />
                Enregistrer la codification
              </button>
            </div>
          ) : null}

          {/* Étape 3 : regroupement */}
          <h3 className="mb-2 mt-6 text-sm font-semibold text-foreground">3. Regroupement par patient et par code acte</h3>
          <PatientCodingSummary patients={data?.summary ?? []} />
        </>
      )}
    </section>
  );
}
